import { z } from 'zod';
import { WHATSAPP_SETUP_STATUSES } from '@/Domain/api/WhatsAppPhoneNumbers/WhatsAppPhoneNumbers.api';
import type { WhatsAppSignupStatus } from '@/Domain/enum/PostHogEvent.enum';

export const WHATSAPP_ATTEMPT_OBSERVATION_MS = 45 * 60_000;

const TERMINAL_SIGNUP_STATUSES = ['connected', 'failed', 'cancelled'] as const;

export const isTerminalSignupStatus = (
  status: WhatsAppSignupStatus,
): status is (typeof TERMINAL_SIGNUP_STATUSES)[number] =>
  (TERMINAL_SIGNUP_STATUSES as readonly WhatsAppSignupStatus[]).includes(status);

const attemptSchema = z.object({
  dialog: z.enum(['embedded_signup', 'permissions']),
  mode: z.enum(['popup', 'redirect']),
  startedAt: z.number().finite(),
  priorStatus: z.enum(WHATSAPP_SETUP_STATUSES),
  stateFingerprint: z.string().max(64).nullable(),
  attemptId: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable(),
  observations: z.array(z.string().max(100)).max(16),
  terminalStatus: z.enum(TERMINAL_SIGNUP_STATUSES).nullable(),
  callbackReason: z.string().max(100).nullable(),
  stageId: z.string().max(64).nullable(),
});

export type WhatsAppConnectAttempt = z.infer<typeof attemptSchema>;

const storageKey = (scenarioId: string): string => `whatsapp_connect_attempt:${scenarioId}`;

export const readWhatsAppConnectAttempt = (scenarioId: string): WhatsAppConnectAttempt | null => {
  try {
    const stored = sessionStorage.getItem(storageKey(scenarioId));
    if (!stored) return null;
    const attempt = attemptSchema.parse(JSON.parse(stored));
    const elapsed = Date.now() - attempt.startedAt;
    return elapsed >= 0 && elapsed < WHATSAPP_ATTEMPT_OBSERVATION_MS ? attempt : null;
  } catch {
    return null;
  }
};

export const writeWhatsAppConnectAttempt = (
  scenarioId: string,
  attempt: WhatsAppConnectAttempt | null,
): void => {
  try {
    if (attempt === null) sessionStorage.removeItem(storageKey(scenarioId));
    else sessionStorage.setItem(storageKey(scenarioId), JSON.stringify(attempt));
  } catch {
    // Blocked storage must not prevent an OAuth launch or its in-memory recovery.
  }
};
